<template>
  <div class="flex-1 flex flex-col overflow-hidden">
    <!-- No publication selected -->
    <div v-if="!store.selectedPublication.value" class="flex-1 flex items-center justify-center p-8">
      <p class="text-sm text-gray-500 italic text-center">
        Select a plan from the sidebar, or create a new one.
      </p>
    </div>

    <!-- Publication selected but no entry -->
    <div v-else-if="!selectedEntry" class="flex-1 flex items-center justify-center p-8">
      <p class="text-sm text-gray-500 italic text-center">
        Select an article from the sidebar to edit its description.
      </p>
    </div>

    <!-- Single entry editor -->
    <div v-else class="flex-1 overflow-y-auto p-4">
      <div class="max-w-2xl mx-auto">
        <!-- Status dot + title -->
        <div class="flex items-center gap-2 mb-4">
          <span
            class="w-2.5 h-2.5 rounded-full shrink-0"
            :class="statusColor(selectedEntry.status)"
            :title="selectedEntry.status"
          ></span>
          <input
            :value="selectedEntry.title"
            @blur="handleTitleBlur"
            @keydown.enter="$event.target.blur()"
            class="flex-1 text-base font-semibold text-gray-900 bg-transparent border-0 border-b border-transparent px-0 py-1 focus:outline-none focus:border-teal-400"
            placeholder="Article title..."
          />
        </div>

        <!-- Description textarea -->
        <textarea
          ref="descriptionRef"
          :key="store.selectedEntryId.value"
          :value="selectedEntry.description"
          @blur="handleDescBlur"
          rows="12"
          class="w-full text-sm text-gray-700 bg-transparent border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-teal-400 resize-y"
          placeholder="Describe what this article should cover..."
        ></textarea>
      </div>
    </div>
  </div>
</template>

<script setup>
import { inject, computed, watch, nextTick, ref } from 'vue'

const store = inject('workbench')
const descriptionRef = ref(null)

const selectedEntry = computed(() => {
  if (!store.selectedPublication.value || !store.selectedEntryId.value) return null
  return store.selectedPublication.value.entries.find(e => e.id === store.selectedEntryId.value) || null
})

// Auto-focus description when selection changes
watch(() => store.selectedEntryId.value, async (newId) => {
  if (newId) {
    await nextTick()
    descriptionRef.value?.focus()
  }
})

function handleTitleBlur(event) {
  const newTitle = event.target.value.trim()
  if (newTitle && newTitle !== selectedEntry.value.title) {
    store.updatePublicationEntry(
      store.selectedPublication.value.id,
      store.selectedEntryId.value,
      { title: newTitle }
    )
  }
}

function handleDescBlur(event) {
  const newDesc = event.target.value
  if (newDesc !== selectedEntry.value.description) {
    store.updatePublicationEntry(
      store.selectedPublication.value.id,
      store.selectedEntryId.value,
      { description: newDesc }
    )
  }
}

function statusColor(status) {
  switch (status) {
    case 'planned': return 'bg-gray-400'
    case 'in_progress': return 'bg-yellow-400'
    case 'published': return 'bg-green-500'
    case 'skipped': return 'bg-red-400'
    default: return 'bg-gray-300'
  }
}
</script>
